// sha-timeline.ts — the SHA timeline modal (seam branch-tree-and-sha-
// timeline; R56, R57 display owner — ruling Q13: the corpus answer is the
// LINKAGE WALK, "will a cut here inherit a corpus or bootstrap?", not a
// tree listing).
//
// R56 ports openForkPicker's behaviour contract (home.ts — the shipped
// 'pick a moment' modal) onto dialog-system's engine for the FROZEN
// /api/vcs/commits wire: a MEASURED header line, per-row badges + tooltips,
// refusals rendered VERBATIM (a 502 mirror failure must never read as "no
// commits" — the fork.ts doctrine, same sentence shape), and
// default-select-newest. The timeline is FIRST-PARENT by the server's
// design: it is exactly the sequence resolveCorpusBase walks, so the sha a
// user picks here is a point ON the corpus walk.
//
// R57 — the corpus answer beside each sha. Cost posture: one
// /api/vcs/corpus-base call is ONE collapsed walk (one batched SELECT), and
// one walk answers a whole SPAN of this page honestly:
//   - a walk anchored at row A that reports lane 'link' at depth D has
//     MEASURED that rows A..A+D-1 carry no link and that row A+D is the
//     linked commit — so row A+i's own answer is 'link at depth D-i', by
//     arithmetic on measured data, not by invention;
//   - a walk that reports 'bootstrap' having walked W < maxDepth hit the
//     END of the chain — every row it covered is definitively bootstrap;
//     at W == maxDepth the derived rows carry a SMALLER measured window,
//     which their tooltip names rather than hides.
// The modal chains anchors (bounded per page) until the page is covered;
// rows past the budget say 'corpus: not walked yet' and resolve when
// selected. The SELECTED sha always gets its OWN walk, and that answer's
// measured sentence ("walked N commits: link hit at depth D → corpus …")
// renders VERBATIM in the answer line — it is already user-grade.
//
// DOM discipline: createElement/textContent throughout (dialog.ts's
// precedent) so fork-picker.test.mjs drives the REAL modal over the
// stub-document harness; the only innerHTML writes are icon() references.

import { openDialog, showDialogMsg, type DialogHandle } from './dialog.js';
import { icon } from './generated/icons.js';
import { mkIconBtn, setBtnState } from './buttons.js';
import { attachElementScrollRail } from 'pi-stack/client/scroll-rails.js';

// ---------------------------------------------------------------------
// The frozen wire (corpus/browse.ts) — mirrored, never reshaped.
// ---------------------------------------------------------------------

export interface VcsCommitRow {
  sha: string;
  author: string;
  authoredDate: string;
  subject: string;
}

export interface VcsCommitsPayload {
  branch: string;
  commits: VcsCommitRow[];
  count: number;
  truncated: boolean;
  nextBefore: string | null;
  firstParent: true;
  freshness: { stale: string | null };
  measured: string;
}

export interface CorpusAnswer {
  resolvedSha: string;
  lane: 'link' | 'bootstrap';
  corpusSha: string | null;
  walked: number;
  depth: number | null;
  maxDepth: number;
  measured: string;
}

/** Walk budget per loaded page — each walk is one server-side SELECT; a
 * page the budget does not cover degrades to named 'not walked yet' rows,
 * never to silence. */
export const CORPUS_WALKS_PER_PAGE = 6;

// ---------------------------------------------------------------------
// Pure decision surfaces (drilled by fork-picker.test.mjs)
// ---------------------------------------------------------------------

export type RowCorpus =
  | { state: 'direct'; answer: CorpusAnswer }
  | {
      state: 'derived'; lane: 'link'; depth: number; corpusSha: string | null;
      anchorSha: string;
    }
  | {
      state: 'derived'; lane: 'bootstrap'; window: number; definitive: boolean;
      anchorSha: string; maxDepth: number;
    }
  | { state: 'pending' };

/** Apply one measured walk (anchored at `anchor`) to the page: exact
 * per-row answers for the span it measured, plus where the NEXT anchor
 * sits (null = the page is covered). Arithmetic on measured data only. */
export function applyWalk(
  commits: readonly VcsCommitRow[],
  anchor: number,
  answer: CorpusAnswer,
): { rows: Map<number, RowCorpus>; nextAnchor: number | null } {
  const rows = new Map<number, RowCorpus>();
  rows.set(anchor, { state: 'direct', answer });
  if (answer.lane === 'link' && answer.depth !== null) {
    for (let i = anchor + 1; i <= anchor + answer.depth && i < commits.length; i++) {
      rows.set(i, {
        state: 'derived', lane: 'link',
        depth: answer.depth - (i - anchor),
        corpusSha: answer.corpusSha,
        anchorSha: answer.resolvedSha,
      });
    }
    const next = anchor + answer.depth + 1;
    return { rows, nextAnchor: next < commits.length ? next : null };
  }
  // bootstrap: W commits measured from the anchor (anchor itself included).
  const w = answer.walked;
  const chainEnded = w < answer.maxDepth;
  for (let i = anchor + 1; i < anchor + w && i < commits.length; i++) {
    rows.set(i, {
      state: 'derived', lane: 'bootstrap',
      window: w - (i - anchor),
      definitive: chainEnded,
      anchorSha: answer.resolvedSha,
      maxDepth: answer.maxDepth,
    });
  }
  if (chainEnded) return { rows, nextAnchor: null };
  const next = anchor + w;
  return { rows, nextAnchor: next < commits.length ? next : null };
}

export interface CorpusChipVm {
  text: string;
  title: string;
  lane: 'link' | 'bootstrap' | 'pending';
}

/** The per-row corpus chip (R57's 'beside each sha' rendering). Direct
 * answers carry the server's measured sentence verbatim in the tooltip;
 * derived answers NAME their anchor and window. */
export function corpusChipVm(rc: RowCorpus): CorpusChipVm {
  if (rc.state === 'direct') {
    const a = rc.answer;
    if (a.lane === 'link') {
      return {
        lane: 'link',
        text: `link · depth ${a.depth ?? '?'} → corpus ${(a.corpusSha ?? '').slice(0, 10) || '?'}`,
        title: a.measured,
      };
    }
    return { lane: 'bootstrap', text: `bootstrap (walked ${a.walked})`, title: a.measured };
  }
  if (rc.state === 'derived' && rc.lane === 'link') {
    return {
      lane: 'link',
      text: `link · depth ${rc.depth} → corpus ${(rc.corpusSha ?? '').slice(0, 10) || '?'}`,
      title:
        `derived from the corpus walk anchored at ${rc.anchorSha.slice(0, 10)}: that walk measured ` +
        `no link above this row, and the linked commit sits ${rc.depth} first-parent commit(s) below this sha`,
    };
  }
  if (rc.state === 'derived') {
    return {
      lane: 'bootstrap',
      text: 'bootstrap',
      title: rc.definitive
        ? `derived from the corpus walk anchored at ${rc.anchorSha.slice(0, 10)}: the first-parent ` +
          `chain ENDS without a corpus link (${rc.window} commit(s) measured from this sha down)`
        : `derived from the corpus walk anchored at ${rc.anchorSha.slice(0, 10)}: no corpus link within ` +
          `the ${rc.window} commit(s) it measured below this sha (that walk capped at ${rc.maxDepth}) — ` +
          'select this sha for its own full walk',
    };
  }
  return {
    lane: 'pending',
    text: 'corpus: not walked yet',
    title: 'the page walk budget did not reach this row — select this sha to run its corpus walk',
  };
}

// The old measured header prose ("N first-parent commit(s) on '<branch>' —
// newest first; this is the corpus walk's own sequence. <measured>") and
// the answer line's verbatim walk sentence are REMOVED per owner item 19
// (batch 2026-09-05, docs/create-release-browsers.md D10). The measured
// sentences survive in the per-row chip tooltips; refusals still render
// verbatim.

/** Item 18 — the lazy-load trigger: within `margin` px of the list end.
 * Pure so the suite drills the exact judgment the scroll handler makes. */
export function lazyShouldLoad(
  scrollTop: number, clientHeight: number, scrollHeight: number, margin = 200,
): boolean {
  return scrollTop + clientHeight >= scrollHeight - margin;
}

/** Local-time display; the ISO stamp stays verbatim in the title. */
export function formatCommitDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// ---------------------------------------------------------------------
// The modal
// ---------------------------------------------------------------------

async function jsonFetch<T>(method: string, url: string, body?: unknown): Promise<{ status: number; data: T | null }> {
  try {
    const res = await fetch(url, {
      method,
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    let data: T | null = null;
    try { data = (await res.json()) as T; } catch { /* tolerate non-JSON */ }
    return { status: res.status, data };
  } catch {
    return { status: 0, data: null };
  }
}

export interface ShaTimelineOptions {
  branch: string;
  /** Hand back the picked sha with the corpus answer resolved for it (null
   * when its own walk had not landed yet — the receiver may ask
   * /api/vcs/corpus-base itself). */
  onPick?: ((sha: string, answer: CorpusAnswer | null) => void) | undefined;
  /** Commits per page (server default 50, capped 200 server-side). */
  count?: number | undefined;
  /** Fired when this dialog's content leaves the host — a real close OR a
   * content swap to another dialog (the engine's semantics; the create-
   * modal wiring's intent machine rides exactly that). */
  onClose?: (() => void) | undefined;
}

export interface ShaTimelineHandle extends DialogHandle {
  /** Settles after the first commits round trip AND the initial corpus
   * walk chain (loaded OR refused — refusals render either way). */
  ready: Promise<void>;
}

export function openShaTimeline(host: HTMLElement, opts: ShaTimelineOptions): ShaTimelineHandle {
  let readyResolve: () => void = () => {};
  const ready = new Promise<void>((res) => { readyResolve = res; });

  const handle = openDialog(host, (body, close) => {
    const title = document.createElement('h2');
    title.className = 'hc-dialog-title';
    title.textContent = `SHA timeline — ${opts.branch}`;
    body.appendChild(title);

    const status = document.createElement('p');
    status.className = 'hc-status hc-st-status';
    status.textContent = 'Loading the SHA timeline…';
    body.appendChild(status);

    const list = document.createElement('div');
    list.className = 'hc-st-list';
    list.setAttribute('role', 'radiogroup');
    list.setAttribute('aria-label', `First-parent commits on ${opts.branch}`);
    list.hidden = true;
    body.appendChild(list);

    const msg = document.createElement('p');
    msg.className = 'hc-dialog-msg';
    body.appendChild(msg);

    // Item 15/18: icon action buttons (name on data-tip = aria-label,
    // never title); 'Load more' is GONE — the list lazy-loads on scroll.
    const actions = document.createElement('div');
    actions.className = 'hc-dialog-actions';
    const cancelBtn = mkIconBtn({ glyph: 'x', label: 'Cancel', attrs: { 'data-act': 'no' } });
    cancelBtn.addEventListener('click', close);
    actions.appendChild(cancelBtn);
    let useBtn: HTMLButtonElement | null = null;
    if (opts.onPick) {
      // THE additive action of this modal (its own view context — the
      // timeline stacks ABOVE the branch browser and the create form, and
      // each of those owns its own additive seat).
      useBtn = mkIconBtn({
        glyph: 'circle-check',
        label: 'Use this SHA',
        intent: 'additive',
        ctx: 'sha-timeline',
        state: 'disabled',
        cls: 'hc-st-use',
        attrs: { 'data-act': 'yes' },
      });
      actions.appendChild(useBtn);
    }
    body.appendChild(actions);

    // ----- state -----
    const commits: VcsCommitRow[] = [];
    const rowCorpus = new Map<number, RowCorpus>();
    const chipEls = new Map<number, HTMLElement>();
    const directBySha = new Map<string, CorpusAnswer>();
    const walksInFlight = new Map<string, Promise<void>>();
    let nextBefore: string | null = null;
    let chosen: string | null = null;
    let chosenIndex = -1;

    const setChip = (index: number): void => {
      const el = chipEls.get(index);
      if (!el) return;
      const rc = rowCorpus.get(index) ?? { state: 'pending' as const };
      const vm = corpusChipVm(rc);
      el.textContent = vm.text;
      el.dataset.tip = vm.title;
      if (el.classList) {
        el.classList.remove('hc-st-chip-link', 'hc-st-chip-boot', 'hc-st-chip-pending');
        el.classList.add(
          vm.lane === 'link' ? 'hc-st-chip-link'
            : vm.lane === 'bootstrap' ? 'hc-st-chip-boot' : 'hc-st-chip-pending');
        if (rc.state !== 'pending') {
          el.classList.remove('hc-st-chip-in');
          el.classList.add('hc-st-chip-in');
        }
      }
    };

    /** One collapsed walk for `index`; the answer annotates its whole
     * measured span. Refusals render by name — never a silent chip. */
    const walkFrom = (index: number): Promise<void> => {
      const c = commits[index];
      if (!c) return Promise.resolve();
      if (directBySha.has(c.sha)) return Promise.resolve();
      const inflight = walksInFlight.get(c.sha);
      if (inflight) return inflight; // one walk per sha, ever (dedupe)
      const p = walkFromUncached(index, c.sha).finally(() => { walksInFlight.delete(c.sha); });
      walksInFlight.set(c.sha, p);
      return p;
    };

    const walkFromUncached = async (index: number, sha: string): Promise<void> => {
      const { status: st, data } = await jsonFetch<CorpusAnswer & { error?: string }>(
        'GET', `/api/vcs/corpus-base?sha=${encodeURIComponent(sha)}`);
      if (st !== 200 || !data || (data.lane !== 'link' && data.lane !== 'bootstrap')) {
        // The refusal renders VERBATIM in the message seat (the answer
        // line is gone — owner item 19; chips carry the measured facts).
        showDialogMsg(msg, `corpus answer unavailable (${st}): ${data?.error ?? 'no response'}`);
        return;
      }
      directBySha.set(sha, data);
      const { rows } = applyWalk(commits, index, data);
      for (const [i, rc] of rows) {
        const cur = rowCorpus.get(i);
        // A direct answer never downgrades to a derived one.
        if (cur?.state === 'direct' && rc.state !== 'direct') continue;
        rowCorpus.set(i, rc);
        setChip(i);
      }
    };

    /** Chain walks from `start` until this page span is covered or the
     * budget runs out (remaining rows stay NAMED as not-walked). Rows an
     * earlier walk already answered (a later page under a page-1 span) are
     * skipped, so one walk is one walk — never one per page. */
    const chainWalks = async (start: number): Promise<void> => {
      let anchor: number | null = start;
      let budget = CORPUS_WALKS_PER_PAGE;
      while (anchor !== null && budget > 0) {
        while (anchor < commits.length && rowCorpus.has(anchor)) anchor++;
        const c = commits[anchor];
        if (!c) break;
        budget--;
        await walkFrom(anchor);
        const answer = directBySha.get(c.sha);
        if (!answer) break; // walk refused; the refusal already rendered
        anchor = applyWalk(commits, anchor, answer).nextAnchor;
      }
    };

    const selectRow = (index: number): void => {
      const c = commits[index];
      if (!c) return;
      chosen = c.sha;
      chosenIndex = index;
      if (useBtn) setBtnState(useBtn, 'enabled');
      const rc = rowCorpus.get(index);
      if (!rc || rc.state !== 'direct') void walkFrom(index);
    };

    const renderRows = (page: VcsCommitRow[], startIndex: number): void => {
      for (let k = 0; k < page.length; k++) {
        const i = startIndex + k;
        const c = page[k]!;
        const opt = document.createElement('label');
        opt.className = 'hc-roleopt hc-st-row hc-st-row-in';
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.setAttribute('name', 'hc-st-sha');
        radio.value = c.sha;
        radio.addEventListener('change', () => selectRow(i));
        opt.appendChild(radio);
        const text = document.createElement('span');
        const l1 = document.createElement('span');
        l1.className = 'hc-roleopt-name hc-st-l1';
        const shaEl = document.createElement('span');
        shaEl.className = 'hc-st-sha';
        shaEl.textContent = c.sha.slice(0, 10);
        shaEl.dataset.tip = c.sha;
        l1.appendChild(shaEl);
        if (i === 0) {
          const tip = document.createElement('span');
          tip.className = 'hc-st-b hc-st-b-tip';
          tip.textContent = 'TIP';
          tip.dataset.tip = `refs/heads/${opts.branch} tip at load time`;
          l1.appendChild(tip);
        }
        const chip = document.createElement('span');
        chip.className = 'hc-st-b hc-st-chip hc-st-chip-pending';
        chipEls.set(i, chip);
        l1.appendChild(chip);
        text.appendChild(l1);
        text.className = 'hc-st-main';
        const l2 = document.createElement('span');
        l2.className = 'hc-roleopt-blurb hc-st-l2';
        l2.textContent = c.subject;
        l2.dataset.tip = c.subject;
        text.appendChild(l2);
        opt.appendChild(text);
        // Item 17: author + datetime in a CONSISTENT meta column per card
        // (never tacked onto the subject) so cards compare at a glance.
        const meta = document.createElement('span');
        meta.className = 'hc-st-meta';
        const author = document.createElement('span');
        author.className = 'hc-st-author';
        author.textContent = c.author;
        author.dataset.tip = c.author;
        meta.appendChild(author);
        const when = document.createElement('span');
        when.className = 'hc-st-date';
        when.textContent = formatCommitDate(c.authoredDate);
        when.dataset.tip = c.authoredDate; // the ISO stamp, verbatim
        meta.appendChild(when);
        opt.appendChild(meta);
        list.appendChild(opt);
        setChip(i);
        if (i === 0) {
          // Default-select-newest (openForkPicker parity).
          radio.checked = true;
          selectRow(0);
        }
      }
    };

    const loadPage = async (before: string | null): Promise<void> => {
      const params = new URLSearchParams({ branch: opts.branch });
      if (opts.count) params.set('count', String(opts.count));
      if (before) params.set('before', before);
      const { status: st, data } = await jsonFetch<VcsCommitsPayload & {
        error?: string; candidates?: string[];
      }>('GET', `/api/vcs/commits?${params.toString()}`);
      if (st !== 200 || !data || !Array.isArray(data.commits)) {
        // VERBATIM refusal — never rendered as "no commits" (the ported
        // openForkPicker doctrine).
        const cand = data?.candidates?.length ? ` — did you mean: ${data.candidates.join(', ')}?` : '';
        const line = `SHA timeline unavailable (${st}): ${data?.error ?? 'no response'}${cand}`;
        if (commits.length === 0) status.textContent = line;
        else showDialogMsg(msg, line);
        return;
      }
      const startIndex = commits.length;
      commits.push(...data.commits);
      nextBefore = data.truncated ? data.nextBefore : null;
      if (commits.length === 0) {
        status.textContent =
          `0 first-parent commits on '${opts.branch}' — ${data.measured}`;
        return;
      }
      // Item 19: the measured header prose is GONE on success — the loading
      // line clears; refusals and the 0-commit line still render verbatim.
      status.hidden = true;
      list.hidden = false;
      renderRows(data.commits, startIndex);
      // A walk measured BEFORE this page landed may span into it: re-apply
      // every cached answer over the extended list, so its derived rows
      // materialise before any new walk spends a call (one walk is one
      // walk — never one per page).
      for (const [sha, answer] of directBySha) {
        const idx = commits.findIndex((c) => c.sha === sha);
        if (idx === -1) continue;
        const { rows } = applyWalk(commits, idx, answer);
        for (const [i, rc] of rows) {
          const cur = rowCorpus.get(i);
          if (cur?.state === 'direct' && rc.state !== 'direct') continue;
          rowCorpus.set(i, rc);
          setChip(i);
        }
      }
      await chainWalks(startIndex);
    };

    // Item 18 — lazy loading: within 200px of the list end the next page
    // loads through the SAME loadPage lane; the in-flight guard means a
    // scroll storm spends exactly one fetch (the suite measures it).
    let pageInFlight = false;
    const maybeLoadNext = (): void => {
      if (nextBefore === null || pageInFlight) return;
      const el = list as unknown as { scrollTop?: number; clientHeight?: number; scrollHeight?: number };
      if (!lazyShouldLoad(el.scrollTop ?? 0, el.clientHeight ?? 0, el.scrollHeight ?? 0)) return;
      pageInFlight = true;
      const cursor = nextBefore;
      void loadPage(cursor).finally(() => { pageInFlight = false; });
    };
    list.addEventListener('scroll', maybeLoadNext);

    if (useBtn) {
      useBtn.addEventListener('click', () => {
        if (!chosen) return;
        const answer = directBySha.get(chosen) ?? null;
        close();
        opts.onPick?.(chosen, answer);
      });
    }

    void loadPage(null).finally(() => {
      // Item 18/13: the app's custom scrollbar on the lazy list — browser
      // only; the stub harness has no rail substrate.
      try {
        if (typeof requestAnimationFrame === 'function'
          && (list as { ownerDocument?: unknown }).ownerDocument) {
          attachElementScrollRail(list, { host: body });
        }
      } catch { /* rail is a nicety; the list still scrolls natively */ }
      readyResolve();
    });
  }, { className: 'hc-st-dialog', ...(opts.onClose ? { onClose: opts.onClose } : {}) });

  return { ...handle, ready };
}
